import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCpu, FiShield, FiTarget, FiZap, FiUser, FiPhone } from '../common/Icons';
import SafeIcon from '../common/SafeIcon';
import { resultsData } from '../data/results';

const questions = [
  {
    id: 1,
    title: "1️⃣ TEMPO & ENERGIA REALI",
    question: "Quanto tempo ed energia mentale puoi dedicare realmente ogni settimana a una nuova opportunità?",
    options: [
      "Meno di 3 ore, arrivo spesso stanco",
      "3–6 ore, posso seguire qualcosa di strutturato",
      "6–10 ore, ho energia per costruire",
      "Più di 10 ore, voglio andare all-in"
    ]
  },
  {
    id: 2,
    title: "2️⃣ OBIETTIVO PRINCIPALE",
    question: "Cosa stai cercando davvero in questo momento?",
    options: [
      "Un sistema che lavori anche quando io non posso",
      "Costruire un business mio, scalabile nel tempo",
      "Un lavoro da casa con orari più flessibili",
      "Capire finalmente quale strada ha senso per me"
    ]
  },
  {
    id: 3,
    title: "3️⃣ COME VUOI LAVORARE",
    question: "Quale modalità senti più compatibile con la tua vita oggi?",
    options: [
      "Sistemi automatici e AI che riducono decisioni",
      "Costruire una struttura / community / rete",
      "Lavorare con persone (contatti, qualifiche, vendite)",
      "Non lo so ancora, voglio chiarezza"
    ]
  },
  {
    id: 4,
    title: "4️⃣ RAPPORTO CON RISCHIO E RESPONSABILITÀ",
    question: "Come ti poni davanti a decisioni e responsabilità?",
    options: [
      "Voglio ridurle al minimo, preferisco sistemi",
      "Decido la direzione, non il lavoro quotidiano",
      "Mi piace agire e interagire ogni giorno",
      "Prima capire bene, poi decidere"
    ]
  }
];

const SurveyPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const calculateResult = (userAnswers) => {
    let m1 = 0, m2 = 0, m3 = 0;
    
    if (userAnswers[0] === 0) m1++;
    else if (userAnswers[0] === 1) { m1++; m3++; }
    else if (userAnswers[0] === 2) { m2++; m3++; }
    else if (userAnswers[0] === 3) m2++;

    if (userAnswers[1] === 0) m1++;
    else if (userAnswers[1] === 1) m2++;
    else if (userAnswers[1] === 2) m3++;

    if (userAnswers[2] === 0) m1++;
    else if (userAnswers[2] === 1) m2++;
    else if (userAnswers[2] === 2) m3++;
    else if (userAnswers[2] === 3) m1++;

    if (userAnswers[3] === 0) m1++;
    else if (userAnswers[3] === 1) m2++;
    else if (userAnswers[3] === 2) m3++;
    else if (userAnswers[3] === 3) m1++;

    if (m1 >= 3) return 'model1';
    if (m3 >= 3) return 'model3';
    if (m2 >= 3) return 'model2';

    const scores = [
      { id: 'model1', score: m1, priority: 3 },
      { id: 'model3', score: m3, priority: 2 },
      { id: 'model2', score: m2, priority: 1 }
    ];

    return scores.sort((a, b) => b.score - a.score || b.priority - a.priority)[0].id;
  };

  const handleOptionSelect = (optionIndex) => {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);
    setTimeout(() => setCurrentStep(prev => prev + 1), 250);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) return;

    setIsAnalyzing(true);
    const resultKey = calculateResult(answers);
    const resultDetails = resultsData[resultKey];

    // Meta Pixel Lead Tracking
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: resultDetails.title,
        status: 'completed'
      });
    }

    // Webhook Integration
    try {
      await fetch("https://services.leadconnectorhq.com/hooks/Vpx5f3HSpRYpSSiV2pL9/webhook-trigger/7771d486-d08a-4d85-9617-e981617c8717", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          assignedModel: resultDetails.title,
          modelId: resultDetails.id,
          timestamp: new Date().toISOString(),
          answers: answers.map((ansIdx, qIdx) => ({
            question: questions[qIdx].question,
            answer: questions[qIdx].options[ansIdx]
          }))
        })
      });
    } catch (error) {
      console.error("Webhook Error:", error);
    }

    // Simulazione analisi e reindirizzamento
    setTimeout(() => {
      navigate('/esito', { 
        state: { 
          resultKey, 
          name: formData.name, 
          phone: formData.phone 
        } 
      });
    }, 3500);
  };

  const progress = ((currentStep + 1) / (questions.length + 1)) * 100;

  if (isAnalyzing) {
    return (
      <div className="min-h-screen bg-black-pure flex flex-col items-center justify-center relative z-50 px-6 text-center">
        <div className="absolute inset-0 bg-orange-500/5 blur-[100px] pointer-events-none"></div>
        <div className="relative mb-12">
          <motion.div 
            animate={{ rotate: 360, scale: [1, 1.1, 1] }} 
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="w-32 h-32 border-2 border-slate-800 border-t-orange-500 rounded-full" 
          />
          <SafeIcon icon={FiCpu} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-orange-500 text-4xl animate-pulse" />
        </div>
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 text-center"
        >
          <h2 className="text-2xl font-black text-white uppercase tracking-[0.4em] italic">Analisi AI in corso...</h2>
          <p className="text-slate-500 font-bold text-xs uppercase tracking-widest">Stiamo incrociando i tuoi dati con i modelli 2026</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black-pure py-8 px-4 md:px-6 relative overflow-hidden font-sans flex flex-col">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-600/5 blur-[150px] rounded-full pointer-events-none"></div>
      
      <div className="container mx-auto max-w-2xl relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center space-x-3 text-slate-500 hover:text-white transition-all group"
          >
            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
              <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
            </div>
            <span className="font-black uppercase tracking-[0.2em] text-[10px]">Indietro</span>
          </button>
          
          <div className="flex items-center space-x-3 bg-slate-900/80 px-5 py-2.5 rounded-2xl border border-white/5">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(249,115,22,0.8)]"></div>
            <span className="text-[10px] font-black text-slate-300 tracking-wider uppercase italic">AI SCANNER</span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-12">
          <div className="flex justify-between items-end mb-3">
            <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest italic">Precisione Profilazione</span>
            <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-white/5 rounded-full h-1.5 p-[1px] border border-white/5">
            <motion.div 
              initial={{ width: 0 }} 
              animate={{ width: `${progress}%` }} 
              transition={{ duration: 0.8, ease: "circOut" }}
              className="h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded-full shadow-[0_0_15px_rgba(249,115,22,0.4)]" 
            />
          </div>
        </div>

        <div className="flex-grow flex items-center justify-center py-4">
          <AnimatePresence mode='wait'>
            {currentStep < questions.length ? (
              <motion.div 
                key={currentStep}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="w-full"
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 px-4 py-1.5 rounded-full mb-6">
                    <SafeIcon icon={FiZap} className="text-orange-500 text-xs" />
                    <span className="text-orange-500 font-black uppercase tracking-[0.2em] text-[10px]">Domanda {currentStep + 1} di 4</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-black text-white mb-4 uppercase tracking-tight leading-tight">{questions[currentStep].title}</h1>
                  <p className="text-slate-400 font-bold text-lg leading-relaxed">{questions[currentStep].question}</p>
                </div>

                <div className="grid gap-4">
                  {questions[currentStep].options.map((option, idx) => (
                    <motion.button
                      key={idx}
                      whileHover={{ scale: 1.01, x: 4 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleOptionSelect(idx)}
                      className="w-full text-left p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-orange-500/40 hover:bg-white/10 transition-all group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center text-xs font-black text-slate-500 group-hover:text-orange-500 group-hover:border-orange-500/30 transition-all">
                            {String.fromCharCode(64 + currentStep + 1)}
                          </div>
                          <span className="text-slate-300 font-bold group-hover:text-white transition-colors">{option}</span>
                        </div>
                        <SafeIcon icon={FiTarget} className="opacity-0 group-hover:opacity-100 text-orange-500 transition-opacity" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div 
                key="form-step"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full"
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 px-4 py-1.5 rounded-full mb-6">
                    <SafeIcon icon={FiShield} className="text-orange-500 text-xs" />
                    <span className="text-orange-500 font-black uppercase tracking-[0.2em] text-[10px]">ULTIMO STEP: ACCESSO PERSONALIZZATO</span>
                  </div>
                  <h1 className="text-3xl font-black text-white mb-4 uppercase tracking-tight">DOVE INVIAMO IL RISULTATO?</h1>
                  <p className="text-slate-400 font-bold">Inserisci i tuoi dati per ricevere l'accesso al modello più adatto a te.</p>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-6 bg-white/5 p-8 rounded-[2rem] border border-white/5 backdrop-blur-md">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Nome Completo</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                        <SafeIcon icon={FiUser} />
                      </div>
                      <input 
                        required
                        type="text" 
                        placeholder="Es: Mario Rossi"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-slate-900 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white focus:outline-none focus:border-orange-500 transition-colors placeholder:text-slate-700"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Numero di Telefono (WhatsApp)</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                        <SafeIcon icon={FiPhone} />
                      </div>
                      <input 
                        required
                        type="tel" 
                        placeholder="Es: +39 333 1234567"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full bg-slate-900 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white focus:outline-none focus:border-orange-500 transition-colors placeholder:text-slate-700"
                      />
                    </div>
                  </div>

                  <button 
                    type="submit"
                    className="btn-premium w-full py-5 rounded-xl font-black text-white text-lg flex items-center justify-center space-x-3 shadow-[0_0_40px_rgba(249,115,22,0.3)]"
                  >
                    <span>Genera il mio Modello</span>
                    <SafeIcon icon={FiZap} />
                  </button>

                  <p className="text-[9px] text-slate-600 text-center uppercase tracking-widest font-bold">
                    Trattiamo i tuoi dati secondo la GDPR e solo per inviarti il contenuto richiesto.
                  </p>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-center items-center space-x-6 pb-4">
          <div className="flex items-center space-x-2 text-slate-600">
            <SafeIcon icon={FiShield} className="text-xs" />
            <span className="text-[8px] uppercase font-black tracking-widest">Privacy Protected</span>
          </div>
          <div className="w-[1px] h-3 bg-white/10"></div>
          <div className="flex items-center space-x-2 text-slate-600">
            <SafeIcon icon={FiCpu} className="text-xs" />
            <span className="text-[8px] uppercase font-black tracking-widest">Neural Analysis Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;